import { useState } from "react"

import { HeaderDiv, TextExample, FieldLabel } from "../../styledComponents/Components.styles"
import { getSelectStyles } from "../../styledComponents/ReactSelectStyles"
import TooltipIcon from "../../styledComponents/TooltipIcon"
import { useDataContext } from "../../DataProvider/DataContext"
import CreatableSelect from 'react-select/creatable';
import { Tooltip } from "@mui/material"
import { ResetValueButton } from "./ResetValueButton"
import { FormFieldProps } from "../../../models/FormFieldProps"
import palette from "../../../utilities/palette"
import WarningTooltipIcon from "../../styledComponents/WarningTooltipIcon"
import { checkIfFieldMatchesArchitecture, checkIfFieldIsPrefilledByCatalog } from "../../../utilities/architectureTypeDataProcess"

export function MultiValueTextField(
    {
        // Essential Information props
        label,
        value,
        placeholder,
        onChange,
        onRevert,
        textExample,
        textHint,
        errorMessage,
        overridenErrorMessage,
        isDisabled,
        required,
        components,
        pathToField,
        isPrefilledValueChanged,
        stylingVariant,

        // ID props
        idLabel,
        idInput,
        idError,

        // name props
        nameInput,
    }: Readonly<FormFieldProps>
) {

    const {architectureType} = useDataContext();
    const isArchitectureTypeMatch = pathToField ? checkIfFieldMatchesArchitecture(pathToField, architectureType) : true;
    const isPrefilledByCatalog = pathToField ? checkIfFieldIsPrefilledByCatalog(pathToField, architectureType) : false;

    // Helper function to determine tooltip title text
    const getTooltipTitle = () => {        
        return "";
    };

    // Helper function to determine placeholder text
    const getPlaceholderText = () => {
        if (!isArchitectureTypeMatch) {
            return "";
        }
        
        return placeholder || "";
    };

    // Helper function to check if field should be disabled based on architecture, prefill status, or external prop
    const shouldFieldBeDisabled = () => {

        if (isDisabled) return true;
        return false;
    };

    const createOption = (label) => ({
        label,
        value: label,
      });

    const [inputValue, setInputValue] = useState('');


    const handleKeyDown = (event) => {
        if (!inputValue) return;
        switch (event.key) {
            case 'Enter':
            case 'Tab':
            onChange([...value, createOption(inputValue)]);
            setInputValue('');
            event.preventDefault();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();

        const pastedText = e?.clipboardData?.getData('text') ?? '';

        if (pastedText.trim()) {
            const newItems = pastedText
            // Split pasted text by common delimiters (newlines, commas, semicolons)
                .split(/[\n,;]+/)
                .map(item => item.trim())
                .filter(item => item.length > 0)
                .map(item => createOption(item));
            
            onChange([...value, ...newItems]);
            
            setInputValue('');
        }
    };

    const innerComponents = {
        DropdownIndicator: null,
    };

    return (
        isArchitectureTypeMatch && 
        <HeaderDiv style={{width: '560px'}}>

                <FieldLabel id={idLabel} variant={stylingVariant}>
                    {label}
                    {required && <span style={{color: palette.stepper.error}}> *</span>}
                    {textExample && <>{" "}<TextExample>{textExample}</TextExample></>}
                    {" "}
                    {textHint && <TooltipIcon content={textHint}/>}
                    {isPrefilledValueChanged === true && isPrefilledByCatalog === true && <WarningTooltipIcon content={"This field's value is different from the registered value"}/>} 
                    {" "}:
                </FieldLabel>
                <Tooltip 
                    placement="top-end"
                    title={getTooltipTitle()}>
                    <div 
                        style={{
                            width: 'fit-content', 
                            display: 'flex', 
                            flexDirection: 'row',
                            margin: '5px 0'
                        }}
                        onPaste={handlePaste}
                    >
                        <CreatableSelect

                            name={nameInput}
                            id={idInput}
                            components={components ?? innerComponents}
                            inputValue={inputValue}
                            placeholder={getPlaceholderText()}
                            isDisabled={shouldFieldBeDisabled()}
                            isClearable
                            isMulti
                            menuIsOpen={false}
                            onChange={onChange}
                            onInputChange={(newValue) => setInputValue(newValue)}
                            onKeyDown={handleKeyDown}
                            value={
                                !isArchitectureTypeMatch ? null : value
                            }
                            styles={getSelectStyles(isArchitectureTypeMatch, stylingVariant, !!errorMessage)}
                        />
                        <ResetValueButton
                            isPrefilledByCatalog={isPrefilledByCatalog ?? false}
                            isPrefilledValueChanged={isPrefilledValueChanged ?? false}
                            onClick={onRevert}
                        />
                    </div>
                </Tooltip>


                {errorMessage && (
                    <span id={idError} style={{ color: palette.stepper.error }}>{
                        overridenErrorMessage ?? errorMessage}</span>
                )}
      </HeaderDiv>
    )
}